import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import LoadingSpinner from '../../common/components/LoadingSpinner';
import { NEW_ITEM_URL_ID } from '../../common/constants';
import { getMessage } from '../../common/language';
import { getEnvAsBoolean, getRoutePath } from '../../common/utility';
import NavLink from './NavLink';

const RecipeImportFromUrlModal = lazy(() => import('./CreateRecipeImportFromUrlModal'));

const messages = defineMessages({
  create_recipe: {
    id: 'nav.create_recipe',
    description: 'Create recipe title',
    defaultMessage: 'Create',
  },
  create_recipe_manually: {
    id: 'nav.create_recipe.manually',
    description: 'Create recipe manually button',
    defaultMessage: 'Create manually',
  },
  import_from_url: {
    id: 'nav.create_recipe.import_from_url',
    description: 'Import recipe from url button',
    defaultMessage: 'Import from url',
  },
});

const CreateRecipeManuallyMenuItem: React.FC = () => {
  const { formatMessage } = useIntl();

  const location = useLocation();

  return (
    <NavLink to={getRoutePath(`/recipe/edit/${NEW_ITEM_URL_ID}`)} active={location.pathname.endsWith(`/recipe/edit/${NEW_ITEM_URL_ID}`)} aria-current={location.pathname.endsWith(`/recipe/edit/${NEW_ITEM_URL_ID}`) ? 'page' : undefined}>{formatMessage(messages.create_recipe_manually)}</NavLink>
  );
};

const CreateRecipeImportFromUrlMenuItem: React.FC = () => {
  const { formatMessage } = useIntl();

  const [showImportFromUrl, setShowImportFromUrl] = useState<boolean>(false);

  const handleImportFromUrlOpenClick  = useCallback(() => { setShowImportFromUrl(true); }, []);
  const handleImportFromUrlDialogClose = useCallback(() => { setShowImportFromUrl(false); }, []);

  return (
    <>
      <NavDropdown.Item eventKey='import-from-url' active={false} onClick={handleImportFromUrlOpenClick}>{`${formatMessage(messages.import_from_url)} …`}</NavDropdown.Item>

      {showImportFromUrl && (
        <Suspense fallback={<LoadingSpinner position='screen-center' />}>
          <RecipeImportFromUrlModal show={showImportFromUrl} onClose={handleImportFromUrlDialogClose} />
        </Suspense>
      )}
    </>
  );
};

const CreateRecipeDropdownMenuItem: React.FC = () => {
  const { formatMessage } = useIntl();

  const location = useLocation();

  return (
    <NavDropdown
        title = {formatMessage(messages.create_recipe)}
        className = 'header-dropdown create-recipe-dropdown'
        active={location.pathname.endsWith(`/recipe/edit/${NEW_ITEM_URL_ID}`)}
        accessKey='n'
        id = 'create-recipe-dropdown'>
      <CreateRecipeManuallyMenuItem />
      <CreateRecipeImportFromUrlMenuItem />
    </NavDropdown>
  );
};

export interface ICreateRecipeMenuItemProps {
  asDropdown: boolean;
}

export const CreateRecipeMenuItem: React.FC<ICreateRecipeMenuItemProps> = ({ asDropdown }: ICreateRecipeMenuItemProps) => {
  const intl = useIntl();

  const disableImportFromUrl = useMemo(() => getEnvAsBoolean('REACT_APP_DISABLE_RECIPE_IMPORT_FROM_URL'), []);

  if (disableImportFromUrl) {
    return <CreateRecipeManuallyMenuItem />;
  } else if (asDropdown) {
    return <CreateRecipeDropdownMenuItem />;
  } else {
    return (
      <>
        <NavDropdown.Divider />
        {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
        <NavDropdown.Header>{getMessage(intl, 'nav.create_recipe')}:</NavDropdown.Header>
        <CreateRecipeManuallyMenuItem />
        <CreateRecipeImportFromUrlMenuItem />
      </>
    );
  }
};

export default CreateRecipeMenuItem;
